import React, { useState, useEffect } from 'react';
import { useParams, Navigate, useSearchParams } from 'react-router-dom';
import { getSeriesBySlug } from '../data/series';
import { fetchSeriesBySlug } from '../services/contentService';
import SeriesHeader from '../components/brand/SeriesHeader';
import SeriesHero from '../components/series/SeriesHero';
import SystemFlow from '../components/series/SystemFlow';
import BookArchitecture from '../components/series/BookArchitecture';
import DashboardPreview from '../components/series/DashboardPreview';
import SeriesPricing from '../components/series/SeriesPricing';
import WhyItWorks from '../components/series/WhyItWorks';
import SeriesAuthor from '../components/series/SeriesAuthor';
import SeriesCTA from '../components/series/SeriesCTA';
import SeriesFooter from '../components/brand/SeriesFooter';
import PaymentStatus from '../components/series/PaymentStatus';
import DemoChat from '../components/series/DemoChat';

const SeriesPage = () => {
  const { seriesSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [showPaymentStatus, setShowPaymentStatus] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState(null);

  // Fetch series data from API with static fallback
  useEffect(() => {
    const loadSeries = async () => {
      setLoading(true);
      // Try API first
      const apiSeries = await fetchSeriesBySlug(seriesSlug);
      if (apiSeries) {
        setSeries(apiSeries);
      } else {
        // Fallback to static data
        const staticSeries = getSeriesBySlug(seriesSlug);
        setSeries(staticSeries);
      }
      setLoading(false);
    };
    loadSeries();
  }, [seriesSlug]);

  // Check for payment return params
  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (payment === 'success' && sessionId) {
      setPaymentSessionId(sessionId);
      setShowPaymentStatus(true);
    } else if (payment === 'cancelled') {
      // User cancelled - just clear params, no modal needed
      const url = new URL(window.location.href);
      url.searchParams.delete('payment');
      window.history.replaceState({}, '', url.pathname);
    }
  }, [searchParams]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-pulse text-zinc-400">Loading...</div>
      </div>
    );
  }

  // If series not found, redirect to home
  if (!series) {
    return <Navigate to="/" replace />;
  }

  const handlePaymentStatusClose = () => {
    setShowPaymentStatus(false);
    setPaymentSessionId(null);
  };

  return (
    <div className="series-page">
      <SeriesHeader series={series} />
      <main>
        <SeriesHero series={series} />
        <SystemFlow series={series} />
        <DemoChat />
        <DashboardPreview series={series} />
        <BookArchitecture series={series} />
        <WhyItWorks series={series} />
        <SeriesPricing series={series} />
        <SeriesCTA series={series} />
      </main>
      <SeriesFooter series={series} />

      {/* Payment Status Modal */}
      {showPaymentStatus && paymentSessionId && (
        <PaymentStatus 
          sessionId={paymentSessionId} 
          onClose={handlePaymentStatusClose}
        />
      )}
    </div>
  );
};

export default SeriesPage;
